/**
 * UI language and date/number format.
 *
 * Two independent choices, both kept on the account (`User.locale`,
 * `User.formatLocale`): the language of the texts, and the format of dates and
 * numbers. A user who never chose a language gets the browser's (Czech for
 * `cs`, English for everything else); one who never chose a format gets the
 * format of the language they have — also after changing it.
 *
 * Texts not translated yet fall back to English (`fallbackLng`).
 */
import i18n from "i18next";
import { useSyncExternalStore } from "react";
import { initReactI18next } from "react-i18next";
import { cs } from "../locales/cs";
import { en } from "../locales/en";
import { workflowActionsCs } from "../locales/parts/workflowActions";

export const LOCALES = ["cs", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export type Preferences = { locale: Locale | null; formatLocale: Locale | null };

const INTL_TAG: Record<Locale, string> = { cs: "cs-CZ", en: "en-US" };

/** Czech for a Czech browser, English for anything else. */
export function browserLocale(): Locale {
  const first =
    typeof navigator === "undefined" ? "" : (navigator.languages?.[0] ?? navigator.language);
  return first?.toLowerCase().startsWith("cs") ? "cs" : "en";
}

function isLocale(v: unknown): v is Locale {
  return v === "cs" || v === "en";
}

let prefs: Preferences = { locale: null, formatLocale: null };
const listeners = new Set<() => void>();

function languageOf(p: Preferences): Locale {
  return p.locale ?? browserLocale();
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en, workflow: {} },
    cs: { translation: cs, workflow: workflowActionsCs },
  },
  lng: languageOf(prefs),
  fallbackLng: "en",
  supportedLngs: [...LOCALES],
  interpolation: { escapeValue: false },
  returnNull: false,
});
if (typeof document !== "undefined") document.documentElement.lang = languageOf(prefs);

type Snapshot = Preferences & { language: Locale; format: Locale };
let snapshot: Snapshot = build(prefs);

function build(p: Preferences): Snapshot {
  const language = languageOf(p);
  return { ...p, language, format: p.formatLocale ?? language };
}

/** Makes `next` the language and format in force, everywhere, at once. */
export function setPreferences(next: { locale?: string | null; formatLocale?: string | null }) {
  prefs = {
    locale: isLocale(next.locale) ? next.locale : null,
    formatLocale: isLocale(next.formatLocale) ? next.formatLocale : null,
  };
  snapshot = build(prefs);
  if (typeof document !== "undefined") document.documentElement.lang = snapshot.language;
  if (i18n.language !== snapshot.language) void i18n.changeLanguage(snapshot.language);
  for (const l of listeners) l();
}

export function getPreferences(): Snapshot {
  return snapshot;
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Chosen values plus the language and format actually in force. */
export function usePreferences(): Snapshot {
  return useSyncExternalStore(subscribe, getPreferences, getPreferences);
}

/** `Intl` tag of the format in force (or of a given one). */
export function intlTag(format: Locale = snapshot.format): string {
  return INTL_TAG[format];
}

/** The one zone the app shows time in; the user cannot change it. */
export const DISPLAY_TZ = "Europe/Prague";

/** Intl separates parts with no-break spaces; text fields want plain ones. */
export function plainSpaces(text: string): string {
  return text.replace(/[\u00a0\u202f]/g, " ");
}

const DATE_OPTS: Record<Locale, Intl.DateTimeFormatOptions> = {
  cs: { day: "numeric", month: "numeric", year: "numeric" },
  en: { day: "numeric", month: "short", year: "numeric" },
};

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

/** 20. 10. 2026 / Oct 20, 2026 (Europe/Prague). */
export function formatDate(
  value: Date | string | number,
  format: Locale = snapshot.format,
  timeZone = DISPLAY_TZ,
): string {
  return plainSpaces(
    new Intl.DateTimeFormat(INTL_TAG[format], { ...DATE_OPTS[format], timeZone }).format(
      toDate(value),
    ),
  );
}

/** 20. 10. 2026 14:30 / Oct 20, 2026, 14:30 (Europe/Prague, 24-hour clock). */
export function formatDateTime(
  value: Date | string | number,
  format: Locale = snapshot.format,
  timeZone = DISPLAY_TZ,
): string {
  return plainSpaces(
    new Intl.DateTimeFormat(INTL_TAG[format], {
      ...DATE_OPTS[format],
      ...TIME_OPTS,
      timeZone,
    }).format(toDate(value)),
  );
}

/** 14:30 (Europe/Prague, 24-hour clock in both formats). */
export function formatTime(
  value: Date | string | number,
  format: Locale = snapshot.format,
  timeZone = DISPLAY_TZ,
  withSeconds = false,
): string {
  return new Intl.DateTimeFormat(INTL_TAG[format], {
    ...TIME_OPTS,
    ...(withSeconds ? { second: "2-digit" } : {}),
    timeZone,
  }).format(toDate(value));
}

/** Month and year heading, e.g. `říjen 2026` / `October 2026`. */
export function formatMonthYear(
  year: number,
  monthIndex: number,
  format: Locale = snapshot.format,
  opts: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" },
): string {
  return new Intl.DateTimeFormat(INTL_TAG[format], {
    ...opts,
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

/** Short weekday names, Monday-first: `po … ne` / `Mon … Sun`. */
export function weekdayLabels(format: Locale = snapshot.format): string[] {
  const f = new Intl.DateTimeFormat(INTL_TAG[format], { weekday: "short", timeZone: "UTC" });
  // 2026-10-19 is a Monday.
  return Array.from({ length: 7 }, (_, i) => f.format(new Date(Date.UTC(2026, 9, 19 + i))));
}

/** Money in the user's number format: `1 234,50 Kč` / `CZK 1,234.50`. */
export function formatMoney(
  amount: number,
  currency = "CZK",
  format: Locale = snapshot.format,
): string {
  return new Intl.NumberFormat(INTL_TAG[format], { style: "currency", currency }).format(amount);
}

/** 1 234,50 / 1,234.50 */
export function formatNumber(
  value: number,
  format: Locale = snapshot.format,
  opts: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 },
): string {
  return new Intl.NumberFormat(INTL_TAG[format], opts).format(value);
}

/** A number with exactly `digits` decimals in the user's number format. */
export function formatFixed(
  value: number,
  digits: number,
  format: Locale = snapshot.format,
): string {
  return formatNumber(value, format, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** The sample shown next to a format choice: `20. 10. 2026, 1 234,50`. */
export function formatExample(format: Locale): string {
  return `${formatDate(new Date(Date.UTC(2026, 9, 20, 12)), format)}, ${formatNumber(1234.5, format)}`;
}

export { i18n };
